import { stationHasEUR } from "../utils/priceFormUtils";
import { useMemo } from "react";

const TF_FUELS = new Set(["TF cig", "TF zboží"]);
const KURZ_GROUP = new Set(["Kurz", "TF zboží", "TF cig"]);

const fuelClass = (value) =>
  `fuel-${String(value).toLowerCase().replace(/\s+/g, "-")}`;

export default function StationRow({
  station,
  columns,
  isArmex,
  showHeaderLabels,
  stationIndex,
  canEdit,
  getInputValue,
  onBlurClearDraft,
  onChangeValue,
  getInputClass,
  discountRenderer,
  eurAllowedFuels,
  eurFuels,
  allowedFuelsForStation,
}) {
  const allowedSet = useMemo(() => {
    const arr = Array.isArray(allowedFuelsForStation) ? allowedFuelsForStation : [];
    return new Set(arr);
  }, [allowedFuelsForStation]);

  const safeColumns = Array.isArray(columns) ? columns : [];
  const hasEURStation = stationHasEUR(station);

  const eurAllowedSet = eurAllowedFuels instanceof Set ? eurAllowedFuels : new Set();
  const eurFuelsSet = eurFuels instanceof Set ? eurFuels : new Set();

  const canShowEurInput = (fuel) => hasEURStation && eurAllowedSet.has(fuel);
  const isFuelAllowed = (fuel) => allowedSet.has(fuel);

  const getRenderedFuels = (col) => {
    const fuels = Array.isArray(col.fuels) ? col.fuels : [];

    return fuels.filter((fuel) => {
      if (!isArmex && TF_FUELS.has(fuel)) return false;
      if (isArmex && TF_FUELS.has(fuel) && !isFuelAllowed(fuel)) return false;
      return true;
    });
  };

  const getColumnSlotCount = (col) => {
    const fuels = getRenderedFuels(col);

    if (col.key === "Kurz" || fuels.some((fuel) => KURZ_GROUP.has(fuel))) {
      return Math.max(fuels.length, 1);
    }

    return fuels.some((fuel) => eurFuelsSet.has(fuel)) ? 2 : 1;
  };

  const renderCell = ({ fuel, currency, showCurLabelTop }) => {
    const topLabel = fuel === "Kurz" && currency === "CZK" ? "Kurz" : currency;

    if (currency === "EUR" && !canShowEurInput(fuel)) {
      return (
        <div key={`${fuel}-${currency}`} className="price-product-slot pricsF-price-field pricsF-empty-cell">
          {showCurLabelTop && <small className="currency-label">EUR</small>}
          <div className="pricsF-empty-placeholder" />
        </div>
      );
    }

    const data = station?.prices?.[fuel]?.[currency];

    if (!data) {
      return (
        <div key={`${fuel}-${currency}`} className="price-product-slot pricsF-price-field pricsF-empty-cell">
          {showCurLabelTop && <small className="currency-label">{topLabel}</small>}
          <div className="pricsF-empty-placeholder" />
        </div>
      );
    }

    return (
      <div key={`${fuel}-${currency}`} className="price-product-slot pricsF-price-field">
        {(showCurLabelTop || !showHeaderLabels) && (
          <small className="currency-label">{topLabel}</small>
        )}

        <input
          type="text"
          inputMode="decimal"
          value={getInputValue(station.id, fuel, currency, data?.value)}
          readOnly={!canEdit || (isArmex && currency === "EUR")}
          onChange={(e) =>
            onChangeValue({
              stationId: station.id,
              fuelType: fuel,
              currency,
              rawValue: e.target.value,
              isArmex,
            })
          }
          onBlur={() => onBlurClearDraft(station.id, fuel, currency)}
          className={`pricsF-fuel-input ${getInputClass({
            station,
            fuel,
            currency,
            value: data?.value,
            confirmed: data?.confirmed,
            isArmex,
          })}`}
        />

        {discountRenderer ? discountRenderer({ station, fuel, currency, data }) : null}
      </div>
    );
  };

  const renderFuelBlock = ({ col, fuel, wantsEurSlot }) => {
    const currs = wantsEurSlot ? ["CZK", "EUR"] : ["CZK"];

    const showSubLabel =
      (col.key === "Kurz" && fuel !== "Kurz") ||
      (showHeaderLabels && TF_FUELS.has(fuel));

    return (
      <div
        key={fuel}
        className={`price-product-subfuel pricsF-subfuel ${
          !station?.prices?.[fuel] ? "is-missing-fuel" : ""
        }`}
      >
        {showSubLabel && <small className="subfuel-label">{fuel}</small>}

        <div className="price-product-subfuel__cells pricsF-currency-row">
          {currs.map((currency) => {
            const showCurLabelTop =
              showHeaderLabels &&
              stationIndex === 0 &&
              fuel === (col.fuels?.[0] ?? fuel);

            return renderCell({ fuel, currency, showCurLabelTop });
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="pricsF-station-block price-station-row-block">
      <div className="pricsF-station-row price-row-grid">
        <strong className="pricsF-station-name price-row-grid__station">
          {station.name}
        </strong>

        <div className="pricsF-prices-row price-row-grid__products">
          {safeColumns.map((col) => {
            const renderedFuels = getRenderedFuels(col);
            const slotCount = getColumnSlotCount(col);

            return (
              <div
                key={col.key}
                className={`price-product-group ${fuelClass(col.key)}`}
                style={{ "--product-slots": slotCount }}
              >
                {!showHeaderLabels && (
                  <label className="fuel-label price-product-group__mobile-label">
                    {col.label}
                  </label>
                )}

                <div className="price-product-group__slots">
                  {renderedFuels.map((fuel) => {
                    const wantsEurSlot = eurFuelsSet.has(fuel);
                    return renderFuelBlock({ col, fuel, wantsEurSlot });
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}